import type { ReverseGeocode } from '@agos/api-client/model'

import type { LatLng } from './delivery-zones'
import { tt } from './i18n'

/**
 * What a pin dropped on a delivery map does - FRONTEND.md §9.694 and §9.705, the owner's #13 and O81.
 *
 * *"... a aj sa bude dať špendlíkom behať po mape a tým aj meniť adresu"*, and then *"That 'Mapa' should be also
 * shown in Checkout for customer."* Two maps - *Nová objednávka*'s and the customer's checkout - and one set of rules,
 * so they live here rather than in either module (§5: modules never import each other):
 *
 *   1. **the pin's own position is the order's point** - `droppedPoint`;
 *   2. **the address text is replaced only by a real place** - `addressFromReverse`;
 *   3. **an answer for a point that is no longer held is dropped** - `answerStillCurrent`.
 *
 * Moved from `@agos/manager-orders`' `pin-drop.ts` (§9.705), which re-exports them. What follows the pin into a
 * zone is each screen's own business: the order builder stores a zone choice and overrides it (`zoneAfterPin`), the
 * checkout derives its zone from the point on every render.
 */

/**
 * The point the pin was put down at, to six decimal places - about ten centimetres.
 *
 * **The pin's own position, not the reverse answer's.** `GET /geocode/reverse` answers the `lat`/`lon` of
 * whatever it named, which can be the middle of the street or the building's centroid; the person put the pin on
 * the door, and the door is what the driver's *Navigovať* goes to (§9.504). Rounded because Leaflet hands back
 * fifteen digits and a stored point is read by people as well as by maps.
 */
export function droppedPoint(raw: LatLng): LatLng {
  return { lat: sixPlaces(raw.lat), lng: sixPlaces(raw.lng) }
}

function sixPlaces(value: number): number {
  return Math.round(value * 1_000_000) / 1_000_000
}

/** Both absent, or the same two numbers. */
export function samePoint(left: LatLng | null, right: LatLng | null): boolean {
  if (left === null || right === null) return left === right
  return left.lat === right.lat && left.lng === right.lng
}

/**
 * The address a reverse lookup found, or `null` to keep the text that is there.
 *
 * **`place_id` is the discriminator, because the endpoint always answers.** When nothing can name the point it
 * answers anyway, with `display_name` set to the bare `"48.2869, 17.7355"` and `place_id: null`
 * (`GeocodeService.coordinatesOnly`) - so writing `display_name` whatever it is would put coordinates into the
 * address box. Google and the local providers always carry an id; only the coordinates-only answer does not.
 */
export function addressFromReverse(answer: unknown): string | null {
  if (typeof answer !== 'object' || answer === null) return null
  const row = answer as Partial<ReverseGeocode>
  if (typeof row.place_id !== 'string' || row.place_id.trim() === '') return null
  const named = typeof row.display_name === 'string' ? row.display_name.trim() : ''
  return named === '' ? null : named
}

/**
 * Whether an answer still describes the point the screen holds.
 *
 * A reverse lookup takes a moment. A second drop, or the address typed over, moves the point on - and an answer
 * for the old point would then write an address the pin is no longer at.
 */
export function answerStillCurrent(asked: LatLng, current: LatLng | null): boolean {
  return samePoint(asked, current)
}

/** Where the last drop's lookup stands. */
export type PinStatus = 'idle' | 'asking' | 'named' | 'unnamed' | 'failed'

/** The one line under the map, so whoever holds the pin knows which of the states it is in. */
export function pinStatusLine(status: PinStatus, hasPoint: boolean): string {
  switch (status) {
    case 'asking':
      return tt('Hľadám adresu...')
    case 'named':
      return tt('Adresa doplnená podľa špendlíka.')
    case 'unnamed':
      return tt('Miesto nemá adresu - text ostal, navigácia pôjde podľa špendlíka.')
    case 'failed':
      return tt('Adresu sa nepodarilo zistiť - bod je uložený, adresu doplňte ručne.')
    case 'idle':
      return hasPoint
        ? tt('Špendlík potiahnite na presné miesto.')
        : tt('Kliknite do mapy tam, kam sa doručuje.')
  }
}
